import { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { FaChevronDown } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import { kollectAITheme } from 'config/theme';
import useClickOutside from 'hooks/useClickOutside';
import { DateType } from '..';

const LogDropdown = ({
  filterKey,
  label,
  selectedValue,
  options = [],
  onChange,
  dates,
  setDates,
}: {
  label: string;
  filterKey: string;
  selectedValue?: any;
  options?: any[];
  onChange: (x: string, y: any) => void;
  dates: DateType;
  setDates: (date: DateType) => void;
}) => {
  const dropdownRef = useRef(null);

  const [showDropdown, setShowDropdown] = useState(false);

  const selectedOption =
    options.find((option) => option.value === selectedValue) || '';

  useClickOutside(dropdownRef, () => showDropdown && setShowDropdown(false));

  return (
    <Box position='relative'>
      <Typography align='center'>{label}</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
          background: '#FFFFFF',
          borderRadius: '8px',
          padding: '0.4rem 0.5rem',
          gap: '0.5rem',
          height: '32px',
          position: 'relative',
        }}
        onClick={() => setShowDropdown(true)}
      >
        <Typography>{selectedOption?.label}</Typography>

        <Box
          sx={{
            position: 'absolute',
            right: '1rem',
            color: '#aaa',
          }}
        >
          <FaChevronDown />
        </Box>
      </Box>

      {showDropdown && (
        <Box
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            zIndex: 10,
            backgroundColor: '#fff',
            boxShadow:
              'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {options.map((option) => (
            <Box
              key={option.label}
              sx={{
                padding: '5px',
                cursor: 'pointer',
                background:
                  selectedOption.label === option.label
                    ? kollectAITheme.primaryColor
                    : kollectAITheme.lightPrimaryColor,
                color: selectedOption.label === option.label ? '#fff' : '#000',
              }}
              onClick={() => {
                onChange(filterKey, option.value);
                if (option.label !== 'Custom') {
                  setDates({ start: null, end: null });
                  setShowDropdown(false);
                }
              }}
            >
              {option.label}
            </Box>
          ))}
          {selectedOption?.label === 'Custom' && (
            <Box
              sx={{
                margin: '5px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              <Box
                sx={{
                  borderTop: '1px solid #aaa',
                  borderBottom: '1px solid #aaa',
                  '& input': { textAlign: 'center', padding: '5px 0' },
                }}
              >
                <DatePicker
                  selected={dates.start}
                  onChange={(start) => {
                    const newDate = { start, end: dates.end };
                    setDates(newDate);
                  }}
                  placeholderText='From'
                />
              </Box>
              <Box
                sx={{
                  borderTop: '1px solid #aaa',
                  borderBottom: '1px solid #aaa',
                  '& input': { textAlign: 'center' },
                }}
              >
                <DatePicker
                  selected={dates.end}
                  onChange={(end) => {
                    const newDate = { start: dates.start, end };
                    setDates(newDate);
                    if (dates?.start) setShowDropdown(false);
                  }}
                  placeholderText='To'
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LogDropdown;
